import { useCallback, useState } from 'react';
import WhitelistService from '../Services/WhitelistService';
import { useUser } from '../Context/UserContext';
import type { AuthorizedTranslatedUser } from '../../Types/schema-helpers';

export const useUserWhitelist = () => {
  const { whiteList, setWhitelist } = useUser();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [apiError, setApiError] = useState<string | null>(null);

  /**
   * Reads the user's whitelist from the API and updates the UserContext state.
   */
  const readWhitelist = useCallback(async (): Promise<boolean> => {
    setIsLoading(true);
    setApiError(null);
    try {
      const response = await WhitelistService.fetchWhitelist();

      if (response && Array.isArray(response.authorized_users)) {
        setWhitelist(response.authorized_users);
        return true;
      }

      setApiError('Invalid whitelist data structure received.');
      return false;
    } catch (error: any) {
      console.error('Error reading whitelist:', error);
      setApiError(error?.message || 'Failed to fetch whitelist.');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [setWhitelist]);

  /**
   * Bulk updates the user's whitelist and immediately updates UserContext with response.
   */
  const updateWhitelist = useCallback(
    async (updatedData: (AuthorizedTranslatedUser | string)[]): Promise<boolean> => {
      if (!Array.isArray(updatedData)) return false;

      setIsLoading(true);
      setApiError(null);
      try {
        // Map objects or string usernames into string[]
        const usernames = updatedData.map((u) => (typeof u === 'string' ? u : u.username));

        const response = await WhitelistService.updateWhitelist(usernames);

        if (response && Array.isArray(response.authorized_users)) {
          setWhitelist(response.authorized_users);
          return true;
        }

        setApiError('Invalid response received from server.');
        return false;
      } catch (error: any) {
        console.error('Error updating whitelist:', error);
        setApiError(error?.message || 'Failed to save whitelist updates.');
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [setWhitelist]
  );

  return { 
    whiteList: whiteList as AuthorizedTranslatedUser[] | null, 
    isLoading, 
    apiError, 
    readWhitelist, 
    updateWhitelist 
  };
};